<template>
  <div :class="$style.view">
    <div
      :class="[$style.view__main, $style.message]"
      v-if="roomId === undefined"
    >
      <OkanekoInsurance :class="$style.message__image" />
      <p :class="$style.message__body">チャットが選択されていません</p>
    </div>
    <template v-else-if="match !== undefined">
      <div :class="[$style.view__main, $style.main]">
        <div
          :class="$style.main__chat"
          ref="messageView"
          @scroll="throttledMessageViewOnScrollHandler"
        >
          <ChatMessageList
            :messages="messages"
            :match="match"
            @change="scrollMessageViewToBottom"
          />
        </div>
      </div>
      <div :class="$style.view__info">
        <UserProfile
          :match="match"
          :diagnosisSummary="diagnosisSummary"
          :karte="karte"
          @openDiagnosisModal="onOpenDiagnosisModal"
          @openUserConcernAndDiagnosisModal="onOpenUserConcernAndDiagnosisModal"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onBeforeUnmount, ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { captureException as newrelicCaptureException } from '@/newrelic'

import UserProfile from '@/components/organisms/UserProfile/UserProfile.vue'
import ChatMessageList from '@/components/organisms/ChatMessageList/ChatMessageList.vue'
import OkanekoInsurance from '@/assets/images/okaneko-insurance.svg?component'
import type { RoomID } from '@/types/app'
import { useMemberChatRoom } from '../hooks/chatView/useMemberChatRoom'
import { useFetchMessages } from '../hooks/chatView/useFetchMessages'
import { useMessageViewScrollingControl } from '../hooks/chatView/useMessageViewScrollingControl'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { useLogger } from '@/logger/logger'
import { useTimeoutPoll } from '@vueuse/core'

const emit = defineEmits<{
  (event: 'update:loading', value: boolean): void
}>()

const logger = useLogger()
const route = useRoute('memberChat')
const router = useRouter()

const roomId = computed((): Maybe<RoomID> =>
  Number.parseInt(route.params.roomId)
)

const { entranceGUID } = route.params
const isDisabledToScrollToBottom = ref<boolean>(false)

const {
  match,
  karte,
  diagnosisSummary,
  loading: chatRoomLoading,
} = useMemberChatRoom(roomId)

const {
  messages,
  fetchPreviousMessages,
  fetchNextMessages,
  loading: messageLoading,
} = useFetchMessages(roomId, isDisabledToScrollToBottom, entranceGUID)

const loading = computed(() => chatRoomLoading.value || messageLoading.value)

watch(loading, () => {
  emit('update:loading', loading.value)
})

const fetchPolling = reactive(
  useTimeoutPoll(async () => {
    try {
      await fetchNextMessages(true)
    } catch (error) {
      if (!isClientError(error)) {
        newrelicCaptureException(error)
      }
    }
  }, 5000)
)

const {
  messageView,
  scrollMessageViewToBottom,
  throttledMessageViewOnScrollHandler,
} = useMessageViewScrollingControl(isDisabledToScrollToBottom, async () => {
  try {
    await fetchPreviousMessages()
  } catch (error) {
    if (!isClientError(error)) {
      newrelicCaptureException(error)
    }
  }
})

useHead({
  title: () =>
    match.value
      ? `${match.value.planner.name} & ${match.value.user.nickname} | 会社 | チャット`
      : ' 会社 | チャット',
})

const onOpenDiagnosisModal = async (): Promise<void> => {
  if (match.value === undefined) {
    return
  }

  await logger.ltlPeople.event.openDiagnosisModal(logger.buildMeta(), {
    data: {
      path: route.fullPath,
      userId: match.value.user.id.toString(),
    },
  })
}

const onOpenUserConcernAndDiagnosisModal = async (): Promise<void> => {
  if (!match.value || !karte.value) {
    return
  }

  await logger.ltlPeople.event.showKarteDetailModal(logger.buildMeta(), {
    data: {
      userId: match.value.user.id,
      karteId: Number(karte.value.id),
    },
  })
}

watch(
  roomId,
  async value => {
    try {
      if (value === undefined) {
        // no id route
        return
      }

      const _roomId = Number(value)

      if (Number.isNaN(_roomId)) {
        // eslint-disable-next-line fp/no-mutating-methods
        await router.push({ name: 'errorBadRequest' })

        return
      }

      isDisabledToScrollToBottom.value = false
    } catch (error) {
      newrelicCaptureException(error)

      // eslint-disable-next-line fp/no-mutating-methods
      await router.push({ name: 'errorNotFound' })
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  fetchPolling.pause()
})
</script>

<style lang="scss" module>
$border: 1px solid $color-border-low-emphasis;

.view {
  display: flex;
  font-weight: bold;

  &__main {
    flex: 1;
  }

  &__info {
    overflow: auto;
    width: 260px;
    background-color: $color-surface-primary;
    box-shadow: $shadow-4;
  }
}

.main {
  display: grid;
  height: 100%;
  grid-template: 1fr auto / 100%;

  &__chat {
    overflow: auto;
  }

  &__actions {
    &:nth-child(n + 2) {
      border-top: $border;
    }
  }
}

.message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &__image {
    width: 200px;
  }

  &__body {
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    font-weight: bold;
    text-align: center;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.actions {
  &__keyphrases {
    padding: 12px 40px;
  }

  &__sender {
    padding: 24px 40px;

    &:nth-child(n + 2) {
      border-top: $border;
    }
  }
}
</style>
